import {FormEvent, useEffect, useId, useRef, useState} from 'react';
import {carolinaWorkflowStore, documentHandlingFee, KNOWN_SERVERS, NewCompanyFileInput, NewJobInput, PAGE_COUNT_FREE_ALLOWANCE} from './carolinaWorkflowStore';
import {friendlyMutationError} from './friendlyMutationError';
import './CompanyFileIntake.css';
import {CompanyDocumentIntake,ReviewedIntake} from './CompanyDocumentIntake';
import {DocumentTitleList,documentTitles} from './DocumentTitleList';

function emptyJob(): NewJobInput {
  return {subject: '', serviceAddress: '', serviceLevel: 'Standard', deadlineDate: '', server: 'Unassigned', pageCount: undefined};
}

type Props = {sourceLocked?:boolean; initialValues?:Partial<NewCompanyFileInput>; onCreated: (fileId: string) => void; openRequest?:number; onCancel?:()=>void; liveCreate?: (input: NewCompanyFileInput, key: string, documents?:File[]) => Promise<string>};

export function CompanyFileIntake({onCreated, liveCreate, openRequest=0,onCancel,initialValues,sourceLocked=false}: Props) {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState(initialValues?.caption ?? '');
  const [plaintiff, setPlaintiff] = useState(initialValues?.plaintiff ?? '');
  const [defendant, setDefendant] = useState(initialValues?.defendant ?? '');
  const [county, setCounty] = useState(initialValues?.county ?? '');
  const [caseNumber, setCaseNumber] = useState(initialValues?.caseNumber ?? '');
  const [court, setCourt] = useState(initialValues?.court ?? '');
  const [stateCode, setStateCode] = useState(initialValues?.stateCode ?? (liveCreate ? '' : 'NC'));
  const [originatingCustomer, setOriginatingCustomer] = useState(initialValues?.originatingCustomer ?? '');
  const [sendingCompanyName, setSendingCompanyName] = useState(initialValues?.sendingCompanyName ?? '');
  const [sourceOrderReference, setSourceOrderReference] = useState(initialValues?.sourceOrderReference ?? '');
  const [jobs, setJobs] = useState<NewJobInput[]>(initialValues?.jobs ?? [emptyJob()]);
  const [documentLists,setDocumentLists]=useState<string[]>(initialValues?.jobs?.map(job=>(job.documentsToServe??[]).join('\n')) ?? ['']);
  const [documents,setDocuments]=useState<File[]>([]);
  const [documentNotice,setDocumentNotice]=useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const headingId = useId();
  const heading=useRef<HTMLHeadingElement>(null);
  const errorNotice=useRef<HTMLParagraphElement>(null);
  useEffect(()=>{if(openRequest)setOpen(true);},[openRequest]);
  useEffect(()=>{if(open){heading.current?.focus({preventScroll:true});heading.current?.scrollIntoView?.({block:'center',behavior:'instant'});}},[open,openRequest]);
  useEffect(()=>{if(error&&!isSubmitting){errorNotice.current?.focus({preventScroll:true});errorNotice.current?.scrollIntoView?.({block:'center',behavior:'instant'});}},[error,isSubmitting]);

  function useReading(review:ReviewedIntake,files:File[]){
    const fields=review.fields;
    if(fields.plaintiff!==undefined)setPlaintiff(fields.plaintiff);
    if(fields.defendant!==undefined)setDefendant(fields.defendant);
    if(fields.caseNumber!==undefined)setCaseNumber(fields.caseNumber);
    if(fields.stateCode!==undefined)setStateCode(fields.stateCode);
    if(fields.county!==undefined)setCounty(fields.county);
    if(fields.court!==undefined)setCourt(fields.court);
    const copyPageCount=review.copyPageCount;
    if(review.recipients.length){
      // Reapplying a reading must not replace edited/manual jobs. Match only the exact
      // recipient and address, retain their facts, and append genuinely new recipients.
      const nextJobs=jobs.map(job=>({...job})),nextLists=[...documentLists],matched=new Set<number>();
      for(const recipient of review.recipients){
        const existing=nextJobs.findIndex((job,index)=>!matched.has(index)&&job.subject===recipient.subject&&job.serviceAddress===recipient.serviceAddress);
        if(existing>=0){matched.add(existing);continue;}
        const blank=nextJobs.findIndex((job,index)=>!job.subject&&!job.serviceAddress&&!job.deadlineDate&&job.pageCount===undefined&&job.witnessFeeAmount===undefined&&job.serviceLevel==='Standard'&&job.server==='Unassigned'&&!nextLists[index]);
        const newJob={...emptyJob(),...recipient};
        if(blank>=0){nextJobs[blank]=newJob;nextLists[blank]=review.documentsToServe?.join('\n')??'';matched.add(blank);}
        else{matched.add(nextJobs.length);nextJobs.push(newJob);nextLists.push(review.documentsToServe?.join('\n')??'');}
      }
      setJobs(nextJobs.map(job=>copyPageCount!==undefined&&job.pageCount===undefined?{...job,pageCount:copyPageCount}:job));
      setDocumentLists(nextLists.map(value=>value||review.documentsToServe?.join('\n')||''));
    }
    else{
      // Only rows still blank take the chosen count; a page count the person typed is kept.
      if(copyPageCount!==undefined)setJobs(current=>current.map(job=>job.pageCount===undefined?{...job,pageCount:copyPageCount}:job));
      if(review.documentsToServe?.length)setDocumentLists(jobs.map(()=>review.documentsToServe!.join('\n')));
    }
    setDocuments(files);setDocumentNotice('Reviewed suggestions applied. Check the completed form and enter service deadlines before saving.');
  }

  function updateJob(index: number, update: Partial<NewJobInput>) {
    setJobs((current) => current.map((job, i) => (i === index ? {...job, ...update} : job)));
  }

  function addJobRow() {
    setJobs((current) => [...current, emptyJob()]);setDocumentLists(current=>[...current,'']);
  }

  function removeJobRow(index: number) {
    setJobs((current) => current.filter((_, i) => i !== index));setDocumentLists(current=>current.filter((_,i)=>i!==index));
  }

  function resetAndClose() {
    setCaption('');
    setPlaintiff(''); setDefendant(''); setCounty('');
    setCaseNumber('');
    setCourt('');
    setStateCode(liveCreate ? '' : 'NC');
    setOriginatingCustomer('');
    setSendingCompanyName('');
    setSourceOrderReference('');
    setJobs([emptyJob()]);setDocumentLists(['']);
    setDocuments([]);setDocumentNotice('');
    setError('');
    setOpen(false);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmittingRef.current) return;
    if (!(liveCreate ? plaintiff.trim() && defendant.trim() : caption.trim()) || !caseNumber.trim() || !court.trim() || (liveCreate && !stateCode.trim())) {
      setError(liveCreate ? 'Enter the plaintiff, defendant, case number, state and court.' : 'Case caption, case number, and court are required.');
      return;
    }
    for (const job of jobs) {
      if (!job.subject.trim() || !job.serviceAddress.trim() || !job.deadlineDate) {
        setError(liveCreate ? 'Each person to serve needs a name, service address and service deadline.' : 'Each service job needs a subject, service address, and deadline date.');
        return;
      }
    }
    isSubmittingRef.current = true;
    setError('');
    setIsSubmitting(true);
    try {
      const fileId = await (liveCreate ?? carolinaWorkflowStore.createCompanyFile)(
        {
          caption: liveCreate ? `${plaintiff.trim()} v. ${defendant.trim()}` : caption.trim(),
          ...(liveCreate ? {plaintiff: plaintiff.trim(), defendant: defendant.trim(), county: county.trim()} : {}),
          caseNumber: caseNumber.trim(),
          court: court.trim(),
          stateCode,
          originatingCustomer: originatingCustomer.trim(),
          sendingCompanyName: sendingCompanyName.trim() || undefined,
          sourceOrderReference: sourceOrderReference.trim(),
          jobs:jobs.map((job,index)=>({...job,documentsToServe:documentTitles(documentLists[index]??'')})),
        },
        crypto.randomUUID(),
        documents,
      );
      onCreated(fileId);
      resetAndClose();
    } catch (creationError) {
      setError(friendlyMutationError(creationError, 'The company file could not be created.'));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  if (!open) {
    return (
      <div className="mcfw-intake-toggle company-intake-toggle">
        <button type="button" className="mcfw-primary" onClick={() => setOpen(true)}>
          + New company file
        </button>
      </div>
    );
  }

  return (
    <section className="mcfw-intake company-file-intake" aria-labelledby={headingId}>
      <h3 id={headingId} ref={heading} tabIndex={-1}>New company file</h3>
      {liveCreate&&<CompanyDocumentIntake onReviewed={useReading} onStorePapers={files=>{setDocuments(files);setDocumentNotice(`Selected PDF${files.length===1?'':'s'} kept for upload after saving. No reading was requested and your form entries are preserved.`)}} disabled={isSubmitting}/>}
      {documentNotice&&<p role="status">{documentNotice}</p>}
      {documents.length>0&&<p>Court papers to attach to every job when saved: <strong>{documents.map(d=>d.name).join(', ')}</strong> <button type="button" disabled={isSubmitting} onClick={()=>{setDocuments([]);setDocumentNotice('Documents removed. Form entries are preserved.')}}>Remove attachment{documents.length===1?'':'s'}</button></p>}
      {liveCreate && <p className="intake-help">Enter the case as it appears on the court papers. Plaintiff, defendant, case number, state, court, person to serve, service address and service deadline are required.</p>}
      <form onSubmit={submit}>
        {error && (
          <p className="mcfw-returned" role="alert" ref={errorNotice} tabIndex={-1}>
            {error}
          </p>
        )}
        <div className="mcfw-file-facts">
          {liveCreate ? <>
            <div className="mcfw-field"><label htmlFor="intake-plaintiff">Plaintiff <span>(required)</span></label><textarea disabled={isSubmitting} id="intake-plaintiff" rows={2} maxLength={4000} required value={plaintiff} onChange={event=>setPlaintiff(event.target.value)} placeholder="Full name of plaintiff or plaintiffs"/></div>
            <div className="mcfw-field"><label htmlFor="intake-defendant">Defendant <span>(required)</span></label><textarea disabled={isSubmitting} id="intake-defendant" rows={2} maxLength={4000} required value={defendant} onChange={event=>setDefendant(event.target.value)} placeholder="Full name of defendant or defendants"/></div>
          </> :
          <div className="mcfw-field">
            <label htmlFor="intake-caption">Case caption</label>
            <input disabled={isSubmitting} id="intake-caption" value={caption} onChange={(event) => setCaption(event.target.value)} placeholder="Smith v. Example Corp." />
          </div>}
          <div className="mcfw-field">
            <label htmlFor="intake-case-number">Case number</label>
            <input disabled={isSubmitting} id="intake-case-number" required={!!liveCreate} maxLength={96} value={caseNumber} onChange={(event) => setCaseNumber(event.target.value)} placeholder="Copy exactly from the court papers" />
          </div>
          {liveCreate && <div className="mcfw-field"><label htmlFor="intake-state">State <span>(required)</span></label><input disabled={isSubmitting} id="intake-state" value={stateCode} onChange={event=>setStateCode(event.target.value)} maxLength={64} placeholder="State of the issuing court" required aria-describedby="intake-court-help"/></div>}
          {liveCreate && <div className="mcfw-field"><label htmlFor="intake-county">County or court district</label><input disabled={isSubmitting} id="intake-county" value={county} onChange={event=>setCounty(event.target.value)} maxLength={191} placeholder="As shown on the court papers"/><small>Leave blank if the court papers do not specify one.</small></div>}
          <div className="mcfw-field">
            <label htmlFor="intake-court">Court</label>
            <input disabled={isSubmitting} id="intake-court" required={!!liveCreate} maxLength={191} value={court} onChange={(event) => setCourt(event.target.value)} placeholder={liveCreate ? "For example, Court of Common Pleas" : "County Superior Court, NC"} />
          </div>
          {!liveCreate && <div className="mcfw-field">
            <label htmlFor="intake-state">Issuing court jurisdiction</label>
            <select disabled={isSubmitting} id="intake-state" value={stateCode} onChange={(event) => setStateCode(event.target.value)}>
              <option value="NC">North Carolina</option>
              <option value="SC">South Carolina</option>
              <option value="OTHER">Other (affidavit requirements not yet loaded)</option>
            </select>
          </div>}
          {liveCreate && <p id="intake-court-help" className="intake-help intake-full-width">State, county and court identify where the case was filed. They may differ from where the papers will be served.</p>}
          <div className="mcfw-field">
            <label htmlFor="intake-customer">Originating customer (optional reference)</label>
            <input disabled={isSubmitting} id="intake-customer" readOnly={sourceLocked} value={originatingCustomer} onChange={(event) => setOriginatingCustomer(event.target.value)} />
          </div>
          <div className="mcfw-field">
            <label htmlFor="intake-sending-company">Sending company (only if routed from another servicing company's customer)</label>
            <input disabled={isSubmitting}
              id="intake-sending-company"
              value={sendingCompanyName}
              onChange={(event) => setSendingCompanyName(event.target.value)}
              placeholder="Leave blank for a file taken directly from your own customer"
            />
          </div>
          <div className="mcfw-field">
            <label htmlFor="intake-source-order">Source order reference (optional)</label>
            <input disabled={isSubmitting} id="intake-source-order" readOnly={sourceLocked} value={sourceOrderReference} onChange={(event) => setSourceOrderReference(event.target.value)} />
          </div>
        </div>

        <h4>{liveCreate ? 'Serve information' : 'SERVICE JOBS'}</h4>{liveCreate && <p className="intake-help">Add one service job for each person or business to be served. Server assignment follows intake review.</p>}
        {jobs.map((job, index) => (
          <div className="mcfw-intake-job" key={index}>
            {liveCreate && <h5>Person or business to serve {index+1}</h5>}
            <div className="mcfw-file-facts">
              <div className="mcfw-field">
                <label htmlFor={`intake-subject-${index}`}>{liveCreate ? 'Person or business to serve' : 'Subject / recipient'}</label>
                <input disabled={isSubmitting}
                  id={`intake-subject-${index}`}
                  value={job.subject}
                  required={!!liveCreate} maxLength={191}
                  onChange={(event) => updateJob(index, {subject: event.target.value})}
                  placeholder="Jordan Rivera (individual defendant)"
                />
              </div>
              <div className="mcfw-field">
                <label htmlFor={`intake-address-${index}`}>Service address</label>
                <textarea disabled={isSubmitting}
                  id={`intake-address-${index}`}
                  value={job.serviceAddress}
                  rows={2} required={!!liveCreate} maxLength={191}
                  onChange={(event) => updateJob(index, {serviceAddress: event.target.value})}
                  placeholder="123 Main St, City, NC 00000"
                />
              </div>
              <DocumentTitleList value={documentLists[index]??''} onChange={value=>setDocumentLists(current=>current.map((text,i)=>i===index?value:text))} disabled={isSubmitting} required={!!liveCreate}/>
              <div className="mcfw-field">
                <label htmlFor={`intake-level-${index}`}>Service level</label>
                <select disabled={isSubmitting} id={`intake-level-${index}`} value={job.serviceLevel} onChange={(event) => updateJob(index, {serviceLevel: event.target.value as 'Rush' | 'Standard'})}>
                  <option value="Standard">Standard</option>
                  <option value="Rush">Rush</option>
                </select>
              </div>
              <div className="mcfw-field">
                <label htmlFor={`intake-deadline-${index}`}>{liveCreate ? 'Service deadline' : 'Deadline date'}</label>
                <input disabled={isSubmitting} id={`intake-deadline-${index}`} type="date" required={!!liveCreate} aria-describedby={liveCreate ? `intake-deadline-help-${index}` : undefined} value={job.deadlineDate} onChange={(event) => updateJob(index, {deadlineDate: event.target.value})} />
                {liveCreate && <small id={`intake-deadline-help-${index}`}>Last day to serve, entered by your staff from the applicable instructions or court requirements. ProFenz does not calculate this date.</small>}
              </div>
              {!liveCreate && <div className="mcfw-field">
                <label htmlFor={`intake-server-${index}`}>Assigned server</label>
                <select disabled={isSubmitting} id={`intake-server-${index}`} value={job.server} onChange={(event) => updateJob(index, {server: event.target.value})}>
                  {KNOWN_SERVERS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>}
              <div className="mcfw-field">
                <label htmlFor={`intake-page-count-${index}`}>Pages in this copy (optional)</label>
                <input disabled={isSubmitting}
                  id={`intake-page-count-${index}`}
                  type="number"
                  min={0}
                  value={job.pageCount ?? ''}
                  onChange={(event) => updateJob(index, {pageCount: event.target.value ? Number(event.target.value) : undefined})}
                  placeholder={liveCreate ? 'Number of pages' : `First ${PAGE_COUNT_FREE_ALLOWANCE} free`}
                />
              </div>
            </div>
            {!liveCreate && typeof job.pageCount === 'number' && job.pageCount > 0 && (
              <p className="mcfw-page-fee">
                {documentHandlingFee(job.pageCount) > 0
                  ? `Document handling fee for this copy: $${documentHandlingFee(job.pageCount).toFixed(2)} (${job.pageCount - PAGE_COUNT_FREE_ALLOWANCE} page${job.pageCount - PAGE_COUNT_FREE_ALLOWANCE === 1 ? '' : 's'} over the free ${PAGE_COUNT_FREE_ALLOWANCE})`
                  : `Within the free ${PAGE_COUNT_FREE_ALLOWANCE}-page allowance — no handling fee.`}
              </p>
            )}
            {jobs.length > 1 && (
              <button type="button" className="mcfw-secondary" disabled={isSubmitting} onClick={() => removeJobRow(index)}>
                Remove this service job
              </button>
            )}
          </div>
        ))}

        <div className="mcfw-actions">
          <button type="button" className="mcfw-secondary" disabled={isSubmitting} onClick={addJobRow}>
            + Add another service job
          </button>
        </div>

        <div className="mcfw-actions intake-save-actions">
          <button type="button" className="mcfw-secondary" disabled={isSubmitting} onClick={onCancel??resetAndClose}>
            {onCancel?'Return without saving':'Cancel'}
          </button>
          <button type="submit" className="mcfw-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : liveCreate ? 'Save file and jobs' : 'Create company file'}
          </button>
        </div>
        {liveCreate && <p className="intake-help">Saving creates the file and its service jobs in New work for intake review. It does not dispatch a server.</p>}
      </form>
    </section>
  );
}
